/**
 * DateTimeUtil.js
 *
 * Centralized date and time formatting utilities for the entire application.
 * All dates from the backend are treated as UTC and converted to the user's local timezone.
 *
 * USAGE:
 * import { formatDateTime, formatDateOnly, formatTimeOnly, formatRelativeTime } from '../utils/DateTimeUtil';
 *
 * // Full date and time in local timezone
 * formatDateTime('2026-02-05T14:30:00')  // "02/05/2026, 9:30:00 AM" (EST)
 *
 * // Date only
 * formatDateOnly('2026-02-05T14:30:00')  // "Feb 5, 2026"
 *
 * // Time only
 * formatTimeOnly('2026-02-05T14:30:00')  // "9:30:00 AM"
 *
 * // Relative time
 * formatRelativeTime('2026-02-05T14:30:00')  // "2 hours ago"
 */

/**
 * Parse a date string from the backend and convert to local Date object.
 * Backend sends LocalDateTime in UTC (e.g., "2026-02-05T14:30:00").
 * This function ensures consistent parsing by treating dates without timezone as UTC.
 *
 * @param {string|Date|null} dateString - The date to parse
 * @returns {Date|null} - Parsed Date object or null if invalid
 */
export const parseToLocalDate = (dateString) => {
  if (!dateString) return null;

  // Already a Date object
  if (dateString instanceof Date) {
    return isNaN(dateString.getTime()) ? null : dateString;
  }

  // Convert to string if needed
  let str = String(dateString);

  // Handle pure date format (YYYY-MM-DD) - treat as local date to avoid off-by-one issues
  if (/^\d{4}-\d{2}-\d{2}$/.test(str)) {
    const [year, month, day] = str.split("-").map(Number);
    return new Date(year, month - 1, day);
  }

  // For datetime strings without timezone info, treat as UTC by appending 'Z'
  // This ensures consistent behavior: backend stores in UTC, we display in local
  if (
    str.includes("T") &&
    !str.endsWith("Z") &&
    !str.includes("+") &&
    !str.includes("-", 10)
  ) {
    str += "Z";
  }

  const date = new Date(str);
  return isNaN(date.getTime()) ? null : date;
};

/**
 * Format a date/time for full display (date + time) in user's local timezone
 *
 * @param {string|Date|null} dateString - The date to format
 * @param {Object} options - Override default formatting options
 * @returns {string} - Formatted date string (e.g., "02/05/2026, 9:30:00 AM")
 */
export const formatDateTime = (dateString, options = {}) => {
  const date = parseToLocalDate(dateString);
  if (!date) return "Unknown";

  const defaultOptions = {
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    hour12: true,
    ...options,
  };

  return date.toLocaleString(undefined, defaultOptions);
};

/**
 * Format a date for display without time, in user's local timezone
 *
 * @param {string|Date|null} dateString - The date to format
 * @param {string} style - 'short' (02/05/2026), 'medium' (Feb 5, 2026), 'long' (February 5, 2026)
 * @returns {string} - Formatted date string
 */
export const formatDateOnly = (dateString, style = "medium") => {
  const date = parseToLocalDate(dateString);
  if (!date) return "Unknown";

  const styleOptions = {
    short: { year: "numeric", month: "2-digit", day: "2-digit" },
    medium: { year: "numeric", month: "short", day: "numeric" },
    long: { year: "numeric", month: "long", day: "numeric" },
  };

  return date.toLocaleDateString(
    undefined,
    styleOptions[style] || styleOptions.medium,
  );
};

/**
 * Format time only, in user's local timezone
 *
 * @param {string|Date|null} dateString - The date to format
 * @param {boolean} includeSeconds - Whether to include seconds (default: true)
 * @returns {string} - Formatted time string (e.g., "9:30:00 AM" or "9:30 AM")
 */
export const formatTimeOnly = (dateString, includeSeconds = true) => {
  const date = parseToLocalDate(dateString);
  if (!date) return "Unknown";

  const options = {
    hour: "2-digit",
    minute: "2-digit",
    hour12: true,
    ...(includeSeconds && { second: "2-digit" }),
  };

  return date.toLocaleTimeString(undefined, options);
};

/**
 * Format a date as relative time (e.g., "2 hours ago", "3 days ago")
 * Falls back to formatted date for older dates
 *
 * @param {string|Date|null} dateString - The date to format
 * @param {boolean} compact - Use compact format (2h ago vs 2 hours ago)
 * @returns {string} - Relative time string
 */
export const formatRelativeTime = (dateString, compact = false) => {
  const date = parseToLocalDate(dateString);
  if (!date) return "Unknown";

  const now = new Date();
  const diffMs = now - date;

  // Handle future dates
  if (diffMs < 0) {
    return formatDateOnly(dateString);
  }

  const diffSeconds = Math.floor(diffMs / 1000);
  const diffMinutes = Math.floor(diffSeconds / 60);
  const diffHours = Math.floor(diffMinutes / 60);
  const diffDays = Math.floor(diffHours / 24);
  const diffWeeks = Math.floor(diffDays / 7);

  if (compact) {
    if (diffMinutes < 1) return "Just now";
    if (diffMinutes < 60) return `${diffMinutes}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    if (diffWeeks < 4) return `${diffWeeks}w ago`;
    return formatDateOnly(dateString, "short");
  }

  if (diffMinutes < 1) return "Just now";
  if (diffMinutes < 60)
    return `${diffMinutes} minute${diffMinutes !== 1 ? "s" : ""} ago`;
  if (diffHours < 24)
    return `${diffHours} hour${diffHours !== 1 ? "s" : ""} ago`;
  if (diffDays < 7) return `${diffDays} day${diffDays !== 1 ? "s" : ""} ago`;
  if (diffWeeks < 4)
    return `${diffWeeks} week${diffWeeks !== 1 ? "s" : ""} ago`;

  return formatDateOnly(dateString);
};

/**
 * Format date for blog display (short month format)
 *
 * @param {string|Date|null} dateString - The date to format
 * @returns {string} - Formatted date (e.g., "Feb 5, 2026")
 */
export const formatDateForBlog = (dateString) => {
  return formatDateOnly(dateString, "medium");
};

/**
 * Format date for sharing content (long format)
 *
 * @param {string|Date|null} dateString - The date to format
 * @returns {string} - Formatted date (e.g., "February 5, 2026")
 */
export const formatDateForSharing = (dateString) => {
  return formatDateOnly(dateString, "long");
};

/**
 * Format detailed timestamp with both relative and absolute time
 * Useful for tooltips or detailed views
 *
 * @param {string|Date|null} dateString - The date to format
 * @returns {Object} - { relative: string, absolute: string }
 */
export const formatTimestampDetailed = (dateString) => {
  const date = parseToLocalDate(dateString);
  if (!date) {
    return { relative: "Unknown", absolute: "Unknown" };
  }

  return {
    relative: formatRelativeTime(dateString),
    absolute: formatDateTime(dateString),
  };
};

/**
 * Format a date for datetime-local input fields
 * Returns format: YYYY-MM-DDTHH:mm
 *
 * @param {string|Date|null} dateString - The date to format
 * @returns {string} - Formatted string for datetime-local input
 */
export const formatForDateTimeInput = (dateString) => {
  const date = parseToLocalDate(dateString);
  if (!date) return "";

  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  const hours = String(date.getHours()).padStart(2, "0");
  const minutes = String(date.getMinutes()).padStart(2, "0");

  return `${year}-${month}-${day}T${hours}:${minutes}`;
};

/**
 * Format a date for sending to the backend (LocalDateTime format)
 * Returns format: YYYY-MM-DDTHH:mm:ss
 *
 * The backend's LocalDateTime columns hold UTC, and parseToLocalDate reads them
 * back as UTC, so the UTC parts are what get written here — emitting the
 * browser's local parts would shift every value by the viewer's offset.
 *
 * @param {string|Date|null} dateString - The date to format
 * @returns {string|null} - Formatted string for backend or null
 */
export const formatForBackend = (dateString) => {
  if (!dateString) return null;

  const date = new Date(dateString);
  if (isNaN(date.getTime())) return null;

  const year = date.getUTCFullYear();
  const month = String(date.getUTCMonth() + 1).padStart(2, "0");
  const day = String(date.getUTCDate()).padStart(2, "0");
  const hours = String(date.getUTCHours()).padStart(2, "0");
  const minutes = String(date.getUTCMinutes()).padStart(2, "0");
  const seconds = String(date.getUTCSeconds()).padStart(2, "0");

  return `${year}-${month}-${day}T${hours}:${minutes}:${seconds}`;
};

/**
 * Check if a date is valid
 *
 * @param {string|Date|null} dateString - The date to check
 * @returns {boolean} - true if valid date
 */
export const isValidDate = (dateString) => {
  return parseToLocalDate(dateString) !== null;
};

/**
 * Compact time ago format (for UI badges, etc.)
 * Alias for formatRelativeTime with compact=true
 *
 * @param {string|Date|null} dateString - The date to format
 * @returns {string} - Compact relative time string
 */
export const formatTimeAgo = (dateString) => {
  return formatRelativeTime(dateString, true);
};

/**
 * Full timestamp format (alias for formatRelativeTime with compact=false)
 *
 * @param {string|Date|null} dateString - The date to format
 * @returns {string} - Relative time string
 */
export const formatTimestamp = (dateString) => {
  return formatRelativeTime(dateString, false);
};

// Default export for backward compatibility
const DateTimeUtil = {
  parseToLocalDate,
  formatDateTime,
  formatDateOnly,
  formatTimeOnly,
  formatRelativeTime,
  formatDateForBlog,
  formatDateForSharing,
  formatTimestampDetailed,
  formatForDateTimeInput,
  formatForBackend,
  isValidDate,
  formatTimeAgo,
  formatTimestamp,
};

export default DateTimeUtil;
